// The Scenario row editor composes DataTable input renderers. State belongs
// here and the only bulk write is the sealed replace-rows contract.
import { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useNavigate,
  useParams,
  type ShouldBlockFn,
} from "@tanstack/react-router";

import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { MAX_PAGE_SIZE } from "../lib/pagination";
import { queryKeys } from "../lib/queryKeys";
import { useVariableDeclarations } from "../lib/useVariableDeclarations";
import {
  parseScenarioRowsSearch,
  type ScenarioRowsSearch,
} from "../lib/routerSearch";
import { useTypedSearch } from "../lib/typedRouter";
import { useWorkingScope } from "../lib/workingScope";
import { Button, Inline, InlineError, Page, Skeleton, useToast } from "../ui";
import { AuthoringLeaveGuard } from "./AuthoringLeaveGuard";
import { ScenarioRowsView } from "./ScenarioRowsView";
import {
  draftFromRow,
  declaredVariableErrors,
  mergeDeclaredVariables,
  replacementRows,
  type DraftRow,
  type ScenarioRow,
} from "./scenarioRowsState";

export async function fetchAllScenarioRows(
  scenarioId: string,
  signal?: AbortSignal,
): Promise<ScenarioRow[]> {
  const collected: ScenarioRow[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/scenario-rows", {
      signal,
      params: {
        query: {
          page_size: MAX_PAGE_SIZE,
          page_token: pageToken,
          scenario_id: scenarioId,
        },
      },
    });
    if (error) throw new ApiRequestError(error);
    collected.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return collected;
}

function comparableSearch(search: unknown): string {
  if (search === null || typeof search !== "object") return "{}";
  return JSON.stringify(
    Object.entries(search)
      .filter(([key, value]) => key !== "import" && value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right)),
  );
}

export const shouldBlockScenarioRowsNavigation: ShouldBlockFn = ({
  current,
  next,
}) =>
  current.routeId !== next.routeId ||
  current.pathname !== next.pathname ||
  comparableSearch(current.search) !== comparableSearch(next.search);

export function ScenarioRows() {
  const params = useParams({ strict: false });
  const scenarioId =
    typeof params.scenarioId === "string" ? params.scenarioId : "";
  const search = useTypedSearch(parseScenarioRowsSearch);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scope = useWorkingScope();
  const toast = useToast();
  const [draftRows, setDraftRows] = useState<DraftRow[] | null>(null);

  const scenario = useQuery({
    enabled: scenarioId.length > 0,
    queryKey: queryKeys.scenarios.detail(scenarioId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/scenarios/{id}", {
        params: { path: { id: scenarioId } },
      });
      if (error) throw new ApiRequestError(error);
      return data;
    },
  });
  const rowsKey = queryKeys.scenarioRows.list({ scenario_id: scenarioId });
  const rows = useQuery({
    enabled: scenarioId.length > 0,
    queryKey: rowsKey,
    queryFn: ({ signal }) => fetchAllScenarioRows(scenarioId, signal),
  });
  const cascadeVariables = useVariableDeclarations(
    scenario.data?.agent_id ?? "",
  );
  const isScopeMismatch =
    scenario.data !== undefined &&
    (scope.agentId !== scenario.data.agent_id || scope.teamId !== undefined);

  useEffect(() => {
    if (!isScopeMismatch) {
      return;
    }
    void navigate({
      replace: true,
      search: (previous) => ({
        ...previous,
        agent: scenario.data.agent_id,
        team: undefined,
      }),
      to: ".",
    });
  }, [isScopeMismatch, navigate, scenario.data]);

  const variables = useMemo(() => {
    const cascade = cascadeVariables.data ?? [];
    return mergeDeclaredVariables(
      cascade.filter((variable) => variable.scope_type === "agent"),
      cascade.filter(
        (variable) =>
          variable.scope_type === "scenario" &&
          variable.scope_id === scenarioId,
      ),
      cascade,
    );
  }, [cascadeVariables.data, scenarioId]);
  const renderingVariables = useMemo(
    () => variables.map((entry) => entry.variable),
    [variables],
  );
  const loadedRows = useMemo(
    () => (rows.data ?? []).map(draftFromRow),
    [rows.data],
  );
  const isDirty = draftRows !== null;

  const save = useMutation({
    mutationFn: async (nextRows: DraftRow[]) => {
      const conflictErrors = declaredVariableErrors(variables);
      if (conflictErrors.length > 0) {
        throw new Error(conflictErrors.join(" "));
      }
      const body = { rows: replacementRows(nextRows, renderingVariables) };
      const { data, error } = await apiClient.POST(
        "/v1/scenarios/{id}:replace-rows",
        { body, params: { path: { id: scenarioId } } },
      );
      if (error) throw new ApiRequestError(error);
      return data.rows;
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(rowsKey, saved);
      setDraftRows(null);
      toast.success("Scenario rows saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.scenarioRows.all,
      });
    },
    onError: (error) => {
      toast.error("Scenario rows were not saved", {
        description: requestErrorDescription(error),
      });
    },
  });

  const queryError = scenario.error ?? rows.error ?? cascadeVariables.error;
  const isRetrying =
    (scenario.isError && scenario.isFetching) ||
    (rows.isError && rows.isFetching) ||
    (cascadeVariables.isError && cascadeVariables.isFetching);
  function retryFailedQueries() {
    if (scenario.isError) void scenario.refetch();
    if (rows.isError) void rows.refetch();
    if (cascadeVariables.isError) void cascadeVariables.refetch();
  }
  const hasLoadedData =
    scenario.data !== undefined &&
    rows.data !== undefined &&
    cascadeVariables.data !== undefined;
  if (queryError && !isDirty && !hasLoadedData) {
    return (
      <Page variant="workspace">
        <Inline gap={2}>
          <InlineError>{requestErrorDescription(queryError)}</InlineError>
          <Button
            disabled={isRetrying}
            type="button"
            variant="secondary"
            size="sm"
            onClick={retryFailedQueries}
          >
            Retry
          </Button>
        </Inline>
      </Page>
    );
  }
  const dependentQueriesPending =
    scenario.data !== undefined && cascadeVariables.isPending;
  if (
    !isDirty &&
    (scenario.isPending || rows.isPending || dependentQueriesPending)
  ) {
    return (
      <Page variant="workspace">
        <Skeleton label="Loading scenario rows" rows={8} shape="table-row" />
      </Page>
    );
  }
  if (isScopeMismatch && !isDirty) {
    return (
      <Page variant="workspace">
        <Skeleton label="Aligning scenario scope" rows={8} shape="table-row" />
      </Page>
    );
  }
  return (
    <>
      <AuthoringLeaveGuard
        isDirty={isDirty}
        isPending={save.isPending}
        shouldBlockFn={shouldBlockScenarioRowsNavigation}
        subject="scenario-row editor"
      />
      <ScenarioRowsView
        scenarioId={scenarioId}
        agentId={scenario.data?.agent_id}
        declaredVariables={variables}
        draftRows={draftRows}
        isImportOpen={search.import === 1}
        isSavePending={save.isPending}
        loadError={queryError ? requestErrorDescription(queryError) : undefined}
        isRetrying={isRetrying}
        onRetry={retryFailedQueries}
        loadedRows={loadedRows}
        onCancel={() => {
          void navigate({
            search: ({ agent, team }) => ({ agent, team }),
            to: "/scenarios",
          });
        }}
        onDraftRowsChange={setDraftRows}
        onImportOpenChange={(isOpen) => {
          void navigate({
            search: (previous: ScenarioRowsSearch) => ({
              ...previous,
              import: isOpen ? (1 as const) : undefined,
            }),
            to: ".",
          });
        }}
        onSave={(nextRows) => {
          save.mutate(nextRows);
        }}
        saveError={
          save.isError ? requestErrorDescription(save.error) : undefined
        }
        variables={renderingVariables}
      />
    </>
  );
}
